/**
 * Forced change-password page (v2).
 *
 * Triggered when loginV2 returns mustChangePassword=true (post admin reset).
 * User types their new permanent password twice; the page calls
 * selfChangePasswordV2 in services/researcherSecurity, which re-wraps every
 * cached raw KEK with the new PDK and rotates authHash on the server.
 *
 * The page intentionally has no "back" or "skip" — the only exit is a
 * successful change. Layouts (Dashboard) gate routing via
 * isMustChangePassword() so the user can't navigate around it.
 */
import React, { useState } from 'react';
import { Box, Button, Card, CardContent, Stack, TextField, Typography, Alert, CircularProgress } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { useHistory } from 'react-router-dom';
import { selfChangePasswordV2, clearMustChangePassword } from '../../services/researcherSecurity';
import { ROOT_ROUTE } from '../../routes/urls';

const MIN_LENGTH = 8;

function passwordValid(p) {
    return (
        p.length >= MIN_LENGTH &&
        /[a-z]/.test(p) &&
        /[A-Z]/.test(p) &&
        /\d/.test(p) &&
        /[^A-Za-z0-9]/.test(p)
    );
}

export default function ChangePassword() {
    const history = useHistory();
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    function localError() {
        if (!oldPassword) return 'investigation.change_password.error_old_required';
        if (!passwordValid(newPassword)) return 'investigation.change_password.error_new_invalid';
        if (newPassword !== confirmPassword) return 'investigation.change_password.error_confirm_mismatch';
        if (newPassword === oldPassword) return 'investigation.change_password.error_same_as_old';
        return null;
    }

    async function handleSubmit(e) {
        e.preventDefault();
        const local = localError();
        if (local) { setError(local); return; }
        setSubmitting(true);
        setError(null);
        try {
            const { jwt } = await selfChangePasswordV2(oldPassword, newPassword);
            localStorage.setItem('jwt', jwt);
            // The new PDK has rewrapped every cached KEK on the server; the raw KEKs we
            // kept in localStorage are still valid (same plaintext material) so encrypted
            // data continues to decrypt without re-login.
            clearMustChangePassword();
            history.replace(ROOT_ROUTE);
        } catch (err) {
            const msg = err?.response?.data?.errorMessage || err?.message || 'Unknown error';
            setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
            <Card sx={{ minWidth: 360, maxWidth: 480, width: '100%' }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom>
                        <Translate id="investigation.change_password.title" />
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        <Translate id="investigation.change_password.explanation" />
                    </Typography>
                    <form onSubmit={handleSubmit} autoComplete="off">
                        <Stack spacing={2} sx={{ mt: 2 }}>
                            <TextField
                                type="password"
                                fullWidth
                                label={<Translate id="investigation.change_password.old_password" />}
                                value={oldPassword}
                                onChange={(e) => setOldPassword(e.target.value)}
                                autoComplete="current-password"
                                inputProps={{ 'data-testid': 'change-password-old' }}
                            />
                            <TextField
                                type="password"
                                fullWidth
                                label={<Translate id="investigation.change_password.new_password" />}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                autoComplete="new-password"
                                helperText={<Translate id="investigation.change_password.new_password_helper" />}
                                inputProps={{ 'data-testid': 'change-password-new' }}
                            />
                            <TextField
                                type="password"
                                fullWidth
                                label={<Translate id="investigation.change_password.confirm_password" />}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                autoComplete="new-password"
                                inputProps={{ 'data-testid': 'change-password-confirm' }}
                            />
                            {error && (
                                <Alert severity="error">
                                    {error.startsWith('investigation.change_password.')
                                        ? <Translate id={error} />
                                        : error}
                                </Alert>
                            )}
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={submitting}
                                startIcon={submitting ? <CircularProgress size={16} /> : null}
                                data-testid="change-password-submit"
                            >
                                <Translate id="investigation.change_password.submit" />
                            </Button>
                        </Stack>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
}
